import {
  BaseError, ContractFunctionRevertedError, UserRejectedRequestError,
  erc20Abi, type Address, type Hash, type Hex,
} from 'viem';
import {arenaAbi, curveAbi, factoryAbi, ledgerAbi, lockerAbi, CREATION_FEE} from '@colosseum/shared';
import {publicClient} from './chain.js';
import {config, type Addresses} from './config.js';
import {wallet, onRightChain, WalletError} from './wallet.js';

/**
 * Every write the site can make. All of them are ordinary transactions from the
 * user's own wallet; the site holds no key and no session, and there is no
 * server in the path.
 *
 * Nothing here takes a contract address from the indexer. The factory, arena,
 * ledger and locker come from the deployment baked into the bundle, and a
 * token's curve, which the indexer does report, is checked against the
 * factory's own record on chain before a trade is built.
 */

export type Stage = 'simulating' | 'approving' | 'signing' | 'pending' | 'done' | 'failed';

export interface Progress {
  (stage: Stage, detail?: string, hash?: Hash): void;
}

/** Extra gas for a buy that will graduate the token. See `buy` for why. */
const GRADUATION_GAS_HEADROOM = 900_000n;

export class TxError extends Error {
  constructor(message: string, options?: {cause?: unknown}) {
    super(message, options);
    this.name = 'TxError';
  }
}

/** The contract's error name behind a failed call, or null when it was not a revert. */
export function revertName(e: unknown): string | null {
  if (!(e instanceof BaseError)) return null;
  const reverted = e.walk((err) => err instanceof ContractFunctionRevertedError);
  return reverted instanceof ContractFunctionRevertedError ? (reverted.data?.errorName ?? null) : null;
}

/** Turns a revert into the contract's own error name rather than a wall of calldata. */
export function describeError(e: unknown): string {
  // The friendly text below hides the detail on purpose; the console keeps it
  // for whoever is debugging. Nothing here is a secret: it is the wallet's or
  // the RPC's own error for the user's own transaction.
  console.error('[colosseum]', e);
  if (e instanceof UserRejectedRequestError) return 'You rejected the request in your wallet.';
  if (e instanceof TxError) return e.message;
  if (e instanceof WalletError) return e.message;
  if (e instanceof BaseError) {
    const reverted = e.walk((err) => err instanceof ContractFunctionRevertedError);
    if (reverted instanceof ContractFunctionRevertedError) {
      const name = reverted.data?.errorName;
      switch (name) {
        case 'CurveGraduated':
          return 'This token has left its curve. Trade it in its pool instead.';
        case 'SlippageExceeded':
          return 'The price moved past your limit. Raise the slippage tolerance or try a smaller size.';
        case 'InsufficientLiquidity':
          return 'The curve cannot pay out that much right now.';
        case 'LaunchesClosed':
          return 'The factory is no longer admitting new launches.';
        case 'CreationFeeNotPaid':
          return 'The launch needs the 0.0005 ETH creation fee.';
        case 'MetadataTooLong':
          return 'One of the fields is longer than the contract allows.';
        case 'InvalidTokenParams':
          return 'A name and a ticker are required.';
        case 'NothingToClaim':
          return 'There is nothing to claim.';
        case 'SaltRequired':
          return 'The launch needs a random salt. Reload the page and try again.';
        case 'PoolAlreadyExists':
          return 'That token address is already taken. Try again; a fresh salt is drawn for every attempt.';
        case 'PeriodOpen':
          return 'This period has not closed yet.';
        case 'InvalidBeacon':
          return 'The beacon for this period was rejected by the contract.';
        case 'PurchasePending':
          return "The previous draw's purchase is still running. Run its next slice first.";
        case 'NothingPending':
          return 'There is no purchase waiting to run.';
        case 'NoProgress':
          return 'The purchase could not advance in this block. Try again in a moment.';
        case 'FallbackNotAvailable':
          return 'The block-hash fallback opens seven days after the period closes.';
        default:
          return name ? `The contract rejected this: ${name}.` : reverted.shortMessage;
      }
    }
    if (/insufficient funds/i.test(e.message)) return 'Not enough ETH to cover the amount plus gas.';
    return e.shortMessage;
  }
  return (e as Error)?.message ?? 'Something went wrong.';
}

function requireWallet(): {address: Address; client: NonNullable<typeof wallet.client>} {
  if (!wallet.address || !wallet.client) throw new WalletError('Connect a wallet first.');
  if (!onRightChain()) throw new WalletError(`Switch your wallet to ${config.chainName} to continue.`);
  return {address: wallet.address, client: wallet.client};
}

/** The contracts this build was made for. Refuses when none are baked in or the indexer contradicts them. */
function requireDeployment(): Addresses {
  if (config.mismatch) {
    throw new TxError('This site and its indexer disagree about which contracts are deployed. Nothing was sent.');
  }
  if (!config.addresses) throw new TxError('No contract addresses are built into this site for this chain yet.');
  return config.addresses;
}

const verifiedCurves = new Map<string, string>();

/**
 * The curve a trade is sent to must be the one the factory launched for the
 * token. The indexer's copy is only a hint; the factory is asked once per token
 * and the answer remembered, because a curve never changes.
 */
async function assertCurve(token: Address, curve: Address): Promise<void> {
  const {factory} = requireDeployment();
  const key = token.toLowerCase();
  let known = verifiedCurves.get(key);
  if (!known) {
    const onChain = await publicClient().readContract({
      address: factory, abi: factoryAbi, functionName: 'curveOf', args: [token],
    });
    known = onChain.toLowerCase();
    if (/^0x0{40}$/.test(known)) throw new TxError('This token was not launched by the Colosseum factory. Nothing was sent.');
    verifiedCurves.set(key, known);
  }
  if (known !== curve.toLowerCase()) {
    throw new TxError("The indexer's record of this token's curve does not match the factory's. Nothing was sent.");
  }
}

async function run(
  progress: Progress,
  simulate: () => Promise<{request: unknown}>,
  label: string,
): Promise<Hash> {
  progress('simulating', label);
  const {request} = await simulate();
  progress('signing', label);
  const {client} = requireWallet();
  const hash = await client.writeContract(request as never);
  progress('pending', label, hash);
  const receipt = await publicClient().waitForTransactionReceipt({hash});
  if (receipt.status !== 'success') throw new TxError('The transaction was mined but reverted.');
  progress('done', label, hash);
  return hash;
}

/**
 * Buys on the curve.
 *
 * `graduates` matters for gas, not just for the copy. The curve tries to
 * graduate itself the instant a buy exhausts it, inside a try/catch, so a gas
 * estimate is free to settle on an amount where the graduation fails and is
 * swallowed — the buy still succeeds, and the token is left stranded on an
 * exhausted curve waiting for someone to call the factory. Observed on a fork:
 * the estimate left 3,753 gas for a graduation that needs about 690,000. When
 * the quote says this buy graduates, the limit is raised deliberately.
 */
export async function buy(
  {token, curve, ethIn, minTokensOut, graduates}: {
    token: Address; curve: Address; ethIn: bigint; minTokensOut: bigint; graduates: boolean;
  },
  progress: Progress,
): Promise<Hash> {
  const {address} = requireWallet();
  progress('simulating', 'Checking the curve with the factory');
  await assertCurve(token, curve);
  return run(
    progress,
    async () => {
      const client = publicClient();
      const sim = await client.simulateContract({
        address: curve, abi: curveAbi, functionName: 'buy', args: [minTokensOut, address],
        value: ethIn, account: address,
      });
      if (graduates) {
        const estimate = await client
          .estimateContractGas({
            address: curve, abi: curveAbi, functionName: 'buy', args: [minTokensOut, address],
            value: ethIn, account: address,
          })
          .catch(() => 0n);
        (sim.request as {gas?: bigint}).gas = (estimate === 0n ? 400_000n : estimate) + GRADUATION_GAS_HEADROOM;
      }
      return sim;
    },
    graduates ? 'Buying and graduating' : 'Buying',
  );
}

/** Sells back to the curve. Needs an allowance first; the site asks for exactly this sale. */
export async function sell(
  {token, curve, tokensIn, minQuoteOut}: {token: Address; curve: Address; tokensIn: bigint; minQuoteOut: bigint},
  progress: Progress,
): Promise<Hash> {
  const {address, client} = requireWallet();
  const pub = publicClient();
  progress('simulating', 'Checking the curve with the factory');
  await assertCurve(token, curve);

  const allowance = await pub.readContract({
    address: token, abi: erc20Abi, functionName: 'allowance', args: [address, curve],
  });
  if (allowance < tokensIn) {
    progress('approving', 'Approving the curve to take these tokens');
    const {request} = await pub.simulateContract({
      address: token, abi: erc20Abi, functionName: 'approve', args: [curve, tokensIn], account: address,
    });
    const approveHash = await client.writeContract(request as never);
    progress('pending', 'Approval', approveHash);
    const receipt = await pub.waitForTransactionReceipt({hash: approveHash});
    if (receipt.status !== 'success') throw new TxError('The approval reverted.');
  }

  return run(
    progress,
    () =>
      pub.simulateContract({
        address: curve, abi: curveAbi, functionName: 'sell', args: [tokensIn, minQuoteOut, address],
        account: address,
      }),
    'Selling',
  );
}

export interface LaunchParams {
  name: string;
  symbol: string;
  logo: string;
  description: string;
  socials: {twitter: string; telegram: string; discord: string; website: string; farcaster: string};
  /** Random, nonzero, fresh for every attempt: it is what makes the token's address unpredictable. */
  salt: Hex;
}

/** Creates a token and its curve. Anything above the creation fee buys on the spot, exempt from the snipe tax. */
export async function launch(
  params: LaunchParams, firstBuyWei: bigint, minTokensOut: bigint, progress: Progress,
): Promise<{hash: Hash; token: Address; curve: Address}> {
  const {address} = requireWallet();
  const {factory} = requireDeployment();
  const pub = publicClient();

  progress('simulating', 'Checking the launch');
  const sim = await pub.simulateContract({
    address: factory, abi: factoryAbi, functionName: 'launch', args: [params, minTokensOut],
    value: CREATION_FEE + firstBuyWei, account: address,
  });
  const [token, curve] = sim.result as [Address, Address];

  progress('signing', 'Creating the token');
  const {client} = requireWallet();
  const hash = await client.writeContract(sim.request as never);
  progress('pending', 'Creating the token', hash);
  const receipt = await pub.waitForTransactionReceipt({hash});
  if (receipt.status !== 'success') throw new TxError('The launch reverted.');
  progress('done', 'Created', hash);
  return {hash, token, curve};
}

/** Pays a creator's whole accrued balance to them. */
export async function claim(progress: Progress): Promise<Hash> {
  const {address} = requireWallet();
  const {ledger} = requireDeployment();
  return run(
    progress,
    () => publicClient().simulateContract({address: ledger, abi: ledgerAbi, functionName: 'claim', account: address}),
    'Claiming',
  );
}

/**
 * Settles a period and runs the first purchase slice in one transaction. Anyone
 * may do this, which is the point: the site offers it so the protocol never
 * depends on the operator's keeper being up. Reverts as a whole when the slice
 * cannot run; `submitSettle` then records the winner on its own.
 */
export async function submitDraw(signature: `0x${string}`, progress: Progress): Promise<Hash> {
  const {address} = requireWallet();
  const {arena} = requireDeployment();
  return run(
    progress,
    () =>
      publicClient().simulateContract({
        address: arena, abi: arenaAbi, functionName: 'draw', args: [signature], account: address,
      }),
    'Settling the period and buying',
  );
}

/** Settles a period without buying: verifies the beacon and records the winner. */
export async function submitSettle(signature: `0x${string}`, progress: Progress): Promise<Hash> {
  const {address} = requireWallet();
  const {arena} = requireDeployment();
  return run(
    progress,
    () =>
      publicClient().simulateContract({
        address: arena, abi: arenaAbi, functionName: 'settle', args: [signature], account: address,
      }),
    'Settling the period',
  );
}

/** Runs one purchase slice for the settled winner. Anyone may, as often as it takes. */
export async function submitExecute(progress: Progress): Promise<Hash> {
  const {address} = requireWallet();
  const {arena} = requireDeployment();
  return run(
    progress,
    () => publicClient().simulateContract({address: arena, abi: arenaAbi, functionName: 'execute', account: address}),
    'Running a purchase slice',
  );
}

/** Collects a graduated pool's fees into the open period. Anyone may do this too. */
export async function collect(token: Address, progress: Progress): Promise<Hash> {
  const {address} = requireWallet();
  const {locker} = requireDeployment();
  return run(
    progress,
    () =>
      publicClient().simulateContract({
        address: locker, abi: lockerAbi, functionName: 'collect', args: [token], account: address,
      }),
    'Collecting pool fees',
  );
}
